import { tool } from 'ai'
import { BACKEND_URL } from '@/lib/config'
import { getAuthToken } from '@/lib/supabase'
import { toolSchemas } from './tool-schemas'

/**
 * Common (platform-agnostic) tools.
 *
 * These tools must not depend on Excel runtime (`window.Excel`) or Google Apps Script.
 * They should be pure helpers that can run in both environments.
 */

type CellRef = { col: number; row: number }

function columnLettersToNumber(letters: string): number {
  let colNum = 0
  const normalized = letters.toUpperCase()
  for (let i = 0; i < normalized.length; i++) {
    const code = normalized.charCodeAt(i)
    // A..Z => 1..26
    if (code < 65 || code > 90) return Number.NaN
    colNum = colNum * 26 + (code - 64)
  }
  return colNum
}

function parseA1CellRef(input: string): CellRef | null {
  const trimmed = input.trim()
  const match = /^(\$?)([A-Za-z]+)(\$?)(\d+)$/.exec(trimmed)
  if (!match) return null

  const colLetters = match[2] ?? ''
  const rowStr = match[4] ?? ''

  const col = columnLettersToNumber(colLetters)
  const row = Number.parseInt(rowStr, 10)

  if (!Number.isFinite(col) || col <= 0) return null
  if (!Number.isFinite(row) || row <= 0) return null

  return { col, row }
}

/**
 * Returns the row/column size for an A1 range like "A1:C10".
 * Accepts optional sheet prefix, e.g. "Sheet1!A1:C10".
 */
export const getRangeSize = tool({
  ...toolSchemas.getRangeSize,
  execute: async ({ address }) => {
    let error: string | undefined

    try {
      const raw = address.trim()
      if (!raw) {
        error = 'Address is required'
        return { error }
      }

      const afterBang = raw.includes('!') ? (raw.split('!').pop() ?? raw) : raw
      const rangePart = afterBang.trim()

      const [left, right] = rangePart.split(':').map((s) => s.trim())
      const start = parseA1CellRef(left)
      const end = parseA1CellRef(right || left)

      if (!start || !end) {
        error = `Invalid A1 address: "${address}"`
        return { error }
      }

      const rowCount = Math.abs(end.row - start.row) + 1
      const columnCount = Math.abs(end.col - start.col) + 1

      return {
        success: true,
        address,
        start,
        end,
        rowCount,
        columnCount,
      }
    } catch (err) {
      error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to parse range "${address}": ${error}` }
    }
  },
})

/**
 * List all available database connectors
 */
export const listConnectors = tool({
  ...toolSchemas.listConnectors,
  execute: async () => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      const response = await fetch(`${BACKEND_URL}/api/connectors`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
      })

      if (!response.ok) {
        return { error: `Failed to fetch connectors: ${response.status}` }
      }

      const data = await response.json()
      const connectors = (data.connectors || []).map(
        (c: { id: string; name: string; type: string }) => ({
          id: c.id,
          name: c.name,
          type: c.type,
        })
      )

      return {
        success: true,
        connectors,
        count: connectors.length,
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to list connectors: ${error}` }
    }
  },
})

/**
 * Query a database using a connector ID
 */
export const queryDatabase = tool({
  ...toolSchemas.queryDatabase,
  execute: async ({ connectorId, query }) => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      const response = await fetch(
        `${BACKEND_URL}/api/connectors/${connectorId}/execute`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({ action: 'query', params: { query } }),
        }
      )

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.error || `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      return {
        success: true,
        rows: result.rows,
        rowCount: result.rowCount,
        fields: result.fields,
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to query database: ${error}` }
    }
  },
})

/**
 * Send an email using a Resend connector ID
 */
export const sendEmail = tool({
  ...toolSchemas.sendEmail,
  execute: async ({ connectorId, to, subject, body, from, replyTo }) => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      // Fetch connector to get config (replyTo, from)
      const connectorResponse = await fetch(
        `${BACKEND_URL}/api/connectors/${connectorId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
        }
      )

      if (!connectorResponse.ok) {
        return {
          error: `Failed to fetch connector: ${connectorResponse.status}`,
        }
      }

      const connectorData = await connectorResponse.json()
      const connector = connectorData.connector || connectorData
      const config = (connector.config || {}) as {
        from?: string
        replyTo?: string
      }

      // Build params: use provided 'from' or fall back to connector config
      const params: Record<string, string> = {
        to,
        subject,
        body,
      }

      // Use provided 'from' parameter if not empty, otherwise use config.from
      if (from) {
        params.from = from
      } else if (config.from) {
        params.from = config.from
      }

      // Use provided 'replyTo' parameter if not empty, otherwise use config.replyTo
      if (replyTo) {
        params.replyTo = replyTo
      } else if (config.replyTo) {
        params.replyTo = config.replyTo
      }

      const response = await fetch(
        `${BACKEND_URL}/api/connectors/${connectorId}/execute`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({ action: 'sendEmail', params }),
        }
      )

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.error || `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      return {
        success: true,
        messageId: result.rows?.[0]?.messageId,
        message: result.rows?.[0]?.message || 'Email sent successfully',
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to send email: ${error}` }
    }
  },
})

/**
 * Send a message to a Slack channel using a Slack connector ID
 */
export const sendSlackMessage = tool({
  ...toolSchemas.sendSlackMessage,
  execute: async ({ connectorId, channel, message }) => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      // Fetch connector to get config (default channel)
      const connectorResponse = await fetch(
        `${BACKEND_URL}/api/connectors/${connectorId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
        }
      )

      if (!connectorResponse.ok) {
        return {
          error: `Failed to fetch connector: ${connectorResponse.status}`,
        }
      }

      const connectorData = await connectorResponse.json()
      const connector = connectorData.connector || connectorData
      const config = (connector.config || {}) as {
        channel?: string
      }

      // Build params: use provided 'channel' or fall back to connector config
      const params: Record<string, string> = {
        message,
      }

      // Use provided 'channel' parameter if not empty, otherwise use config.channel
      if (channel) {
        params.channel = channel
      } else if (config.channel) {
        params.channel = config.channel
      } else {
        return {
          error:
            'Channel is required (either in connector config or as parameter)',
        }
      }

      const response = await fetch(
        `${BACKEND_URL}/api/connectors/${connectorId}/execute`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({ action: 'sendMessage', params }),
        }
      )

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.error || `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      return {
        success: true,
        ts: result.rows?.[0]?.ts,
        message: result.rows?.[0]?.message || 'Message sent successfully',
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to send Slack message: ${error}` }
    }
  },
})

// =========================================================================
// Stripe tools
// =========================================================================

async function executeStripeAction(
  connectorId: string,
  action: string,
  params: Record<string, unknown>
) {
  const authToken = await getAuthToken()
  if (!authToken) return { error: 'Not authenticated' }
  if (!BACKEND_URL) return { error: 'Backend URL not configured' }

  const response = await fetch(
    `${BACKEND_URL}/api/connectors/${connectorId}/execute`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify({ action, params }),
    }
  )

  const result = await response.json()

  if (!response.ok) {
    return {
      error: result.error || `HTTP ${response.status}: ${response.statusText}`,
    }
  }

  return {
    success: true,
    rows: result.rows,
    rowCount: result.rowCount,
    fields: result.fields,
  }
}

export const queryStripeCustomers = tool({
  ...toolSchemas.queryStripeCustomers,
  execute: async ({ connectorId, limit, email }) => {
    try {
      const params: Record<string, unknown> = {}
      if (limit) params.limit = limit
      if (email) params.email = email
      return await executeStripeAction(connectorId, 'listCustomers', params)
    } catch (err) {
      return {
        error: `Failed to query Stripe customers: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const queryStripePayments = tool({
  ...toolSchemas.queryStripePayments,
  execute: async ({ connectorId, limit, customer }) => {
    try {
      const params: Record<string, unknown> = {}
      if (limit) params.limit = limit
      if (customer) params.customer = customer
      return await executeStripeAction(connectorId, 'listPayments', params)
    } catch (err) {
      return {
        error: `Failed to query Stripe payments: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const queryStripeInvoices = tool({
  ...toolSchemas.queryStripeInvoices,
  execute: async ({ connectorId, limit, customer, status }) => {
    try {
      const params: Record<string, unknown> = {}
      if (limit) params.limit = limit
      if (customer) params.customer = customer
      if (status) params.status = status
      return await executeStripeAction(connectorId, 'listInvoices', params)
    } catch (err) {
      return {
        error: `Failed to query Stripe invoices: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const queryStripeSubscriptions = tool({
  ...toolSchemas.queryStripeSubscriptions,
  execute: async ({ connectorId, limit, customer, status }) => {
    try {
      const params: Record<string, unknown> = {}
      if (limit) params.limit = limit
      if (customer) params.customer = customer
      if (status) params.status = status
      return await executeStripeAction(connectorId, 'listSubscriptions', params)
    } catch (err) {
      return {
        error: `Failed to query Stripe subscriptions: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const getStripeBalance = tool({
  ...toolSchemas.getStripeBalance,
  execute: async ({ connectorId }) => {
    try {
      return await executeStripeAction(connectorId, 'getBalance', {})
    } catch (err) {
      return {
        error: `Failed to get Stripe balance: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const queryStripeProducts = tool({
  ...toolSchemas.queryStripeProducts,
  execute: async ({ connectorId, limit, active }) => {
    try {
      const params: Record<string, unknown> = {}
      if (limit) params.limit = limit
      if (active !== undefined) params.active = active
      return await executeStripeAction(connectorId, 'listProducts', params)
    } catch (err) {
      return {
        error: `Failed to query Stripe products: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const queryStripePayouts = tool({
  ...toolSchemas.queryStripePayouts,
  execute: async ({ connectorId, limit, status }) => {
    try {
      const params: Record<string, unknown> = {}
      if (limit) params.limit = limit
      if (status) params.status = status
      return await executeStripeAction(connectorId, 'listPayouts', params)
    } catch (err) {
      return {
        error: `Failed to query Stripe payouts: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

/**
 * Fetches data from a URL
 * Supports JSON, CSV, HTML, images, and text content
 */
export const fetchDataFromUrl = tool({
  ...toolSchemas.fetchDataFromUrl,
  execute: async ({ url, format }) => {
    let error: string | undefined

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: '*/*',
        },
      })

      if (!response.ok) {
        error = `HTTP ${response.status}: ${response.statusText}`
        return { error }
      }

      const contentType = response.headers.get('content-type') || ''
      const detectedFormat =
        format === 'auto'
          ? contentType.includes('application/json')
            ? 'json'
            : contentType.includes('text/csv') || url.endsWith('.csv')
              ? 'csv'
              : contentType.startsWith('image/')
                ? 'image'
                : 'text'
          : format

      if (detectedFormat === 'image') {
        // For images, return as base64 data URL
        const blob = await response.blob()
        const reader = new FileReader()
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onloadend = () => {
            if (typeof reader.result === 'string') {
              resolve(reader.result)
            } else {
              reject(new Error('Failed to convert image to base64'))
            }
          }
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })

        return {
          success: true,
          url,
          format: 'image',
          contentType,
          dataUrl: base64,
          size: blob.size,
        }
      }

      if (detectedFormat === 'json') {
        const json = await response.json()
        return {
          success: true,
          url,
          format: 'json',
          contentType,
          data: json,
        }
      }

      if (detectedFormat === 'csv') {
        const text = await response.text()
        // Parse CSV into 2D array
        const lines = text.split('\n').filter((line) => line.trim())
        const rows = lines.map((line) => {
          // Simple CSV parsing (handles quoted fields)
          const values: string[] = []
          let current = ''
          let inQuotes = false

          for (let i = 0; i < line.length; i++) {
            const char = line[i]
            if (char === '"') {
              inQuotes = !inQuotes
            } else if (char === ',' && !inQuotes) {
              values.push(current.trim())
              current = ''
            } else {
              current += char
            }
          }
          values.push(current.trim())
          return values
        })

        return {
          success: true,
          url,
          format: 'csv',
          contentType,
          rows,
          rowCount: rows.length,
          columnCount: rows[0]?.length ?? 0,
        }
      }

      // Default: return as text
      const text = await response.text()
      return {
        success: true,
        url,
        format: 'text',
        contentType,
        text,
        length: text.length,
      }
    } catch (err) {
      error = err instanceof Error ? err.message : String(err)
      return {
        error: `Failed to fetch data from "${url}": ${error}`,
      }
    }
  },
})

/**
 * Searches the web using Google Custom Search API
 */
export const webSearch = tool({
  ...toolSchemas.webSearch,
  execute: async ({ query, numResults = 5 }) => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      const response = await fetch(`${BACKEND_URL}/api/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ query, numResults }),
      })

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.error || `HTTP ${response.status}: ${response.statusText}`,
          detail: result.detail,
        }
      }

      return result
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Web search failed: ${error}` }
    }
  },
})

/**
 * List Gmail messages with optional filtering
 */
export const listGmailMessages = tool({
  ...toolSchemas.listGmailMessages,
  execute: async ({ query, maxResults = 10 }) => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      const params = new URLSearchParams()
      params.append('max_results', String(maxResults))
      if (query) {
        params.append('query', query)
      }

      const response = await fetch(
        `${BACKEND_URL}/api/gmail/messages?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
        }
      )

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.detail ||
            result.error ||
            `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      return {
        success: true,
        messages: result.messages,
        messageCount: result.messages?.length || 0,
        nextPageToken: result.nextPageToken,
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to list Gmail messages: ${error}` }
    }
  },
})

/**
 * Get a specific Gmail message by ID
 */
export const getGmailMessage = tool({
  ...toolSchemas.getGmailMessage,
  execute: async ({ messageId }) => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      const response = await fetch(
        `${BACKEND_URL}/api/gmail/messages/${messageId}?format=full`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
        }
      )

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.detail ||
            result.error ||
            `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      // Parse message headers for easier access
      const headers = result.payload?.headers || []
      const getHeader = (name: string) =>
        headers.find(
          (h: { name: string; value: string }) =>
            h.name.toLowerCase() === name.toLowerCase()
        )?.value

      // Extract body text
      let bodyText = ''
      const extractText = (part: {
        mimeType?: string
        body?: { data?: string }
        parts?: unknown[]
      }): string => {
        if (part.mimeType === 'text/plain' && part.body?.data) {
          return atob(part.body.data.replace(/-/g, '+').replace(/_/g, '/'))
        }
        if (part.parts) {
          for (const subpart of part.parts) {
            const text = extractText(
              subpart as {
                mimeType?: string
                body?: { data?: string }
                parts?: unknown[]
              }
            )
            if (text) return text
          }
        }
        return ''
      }

      if (result.payload) {
        bodyText = extractText(result.payload)
      }

      return {
        success: true,
        id: result.id,
        threadId: result.threadId,
        subject: getHeader('Subject') || '(no subject)',
        from: getHeader('From') || '',
        to: getHeader('To') || '',
        date: getHeader('Date') || '',
        snippet: result.snippet,
        body: bodyText,
        labelIds: result.labelIds,
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to get Gmail message: ${error}` }
    }
  },
})

/**
 * Search Gmail messages with query
 */
export const searchGmailMessages = tool({
  ...toolSchemas.searchGmailMessages,
  execute: async ({ query, maxResults = 20 }) => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      const response = await fetch(`${BACKEND_URL}/api/gmail/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ query, max_results: maxResults }),
      })

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.detail ||
            result.error ||
            `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      return {
        success: true,
        messages: result.messages,
        messageCount: result.messages?.length || 0,
        query,
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to search Gmail: ${error}` }
    }
  },
})

/**
 * List Gmail labels
 */
export const listGmailLabels = tool({
  ...toolSchemas.listGmailLabels,
  execute: async () => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      const response = await fetch(`${BACKEND_URL}/api/gmail/labels`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
      })

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.detail ||
            result.error ||
            `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      return {
        success: true,
        labels: result.labels,
        labelCount: result.labels?.length || 0,
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to list Gmail labels: ${error}` }
    }
  },
})

/**
 * Send an email via Gmail (requires read_send permission on connector)
 * Exported separately — only included when user's Gmail connector has send permission.
 */
export const sendGmailMessage = tool({
  ...toolSchemas.sendGmailMessage,
  execute: async ({ to, subject, body, cc, bcc, replyToMessageId }) => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      const response = await fetch(`${BACKEND_URL}/api/gmail/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          to,
          subject,
          body,
          cc: cc || undefined,
          bcc: bcc || undefined,
          reply_to_message_id: replyToMessageId || undefined,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.detail ||
            result.error ||
            `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      return {
        success: true,
        id: result.id,
        threadId: result.threadId,
        message: 'Email sent successfully',
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to send Gmail message: ${error}` }
    }
  },
})

/**
 * Create a LinkedIn post (requires profile_post permission on connector)
 * Exported separately — only included when user's LinkedIn connector has post permission.
 */
export const createLinkedInPost = tool({
  ...toolSchemas.createLinkedInPost,
  execute: async ({ text }) => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        return { error: 'Not authenticated' }
      }

      if (!BACKEND_URL) {
        return { error: 'Backend URL not configured' }
      }

      const response = await fetch(`${BACKEND_URL}/api/linkedin/post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ text }),
      })

      const result = await response.json()

      if (!response.ok) {
        return {
          error:
            result.detail ||
            result.error ||
            `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      return {
        success: true,
        id: result.id,
        message: 'LinkedIn post created successfully',
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      return { error: `Failed to create LinkedIn post: ${error}` }
    }
  },
})

// =========================================================================
// Helper for Google Workspace API calls
// =========================================================================
async function googleWorkspaceApiCall(
  endpoint: string,
  options?: { method?: string; body?: unknown }
) {
  const authToken = await getAuthToken()
  if (!authToken) return { error: 'Not authenticated' }
  if (!BACKEND_URL) return { error: 'Backend URL not configured' }

  const fetchOptions: RequestInit = {
    method: options?.method || 'GET',
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
  }
  if (options?.body) {
    fetchOptions.body = JSON.stringify(options.body)
  }

  const response = await fetch(`${BACKEND_URL}${endpoint}`, fetchOptions)
  const result = await response.json()

  if (!response.ok) {
    return {
      error:
        result.detail ||
        result.error ||
        `HTTP ${response.status}: ${response.statusText}`,
    }
  }
  return { success: true, ...result }
}

// =========================================================================
// Google Calendar tools
// =========================================================================
export const listCalendars = tool({
  ...toolSchemas.listCalendars,
  execute: async () => {
    try {
      return await googleWorkspaceApiCall('/api/google-calendar/calendars')
    } catch (err) {
      return {
        error: `Failed to list calendars: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const listCalendarEvents = tool({
  ...toolSchemas.listCalendarEvents,
  execute: async ({
    calendarId = 'primary',
    timeMin,
    timeMax,
    maxResults = 25,
  }) => {
    try {
      const params = new URLSearchParams()
      params.append('calendar_id', calendarId)
      params.append('max_results', String(maxResults))
      if (timeMin) params.append('time_min', timeMin)
      if (timeMax) params.append('time_max', timeMax)
      return await googleWorkspaceApiCall(
        `/api/google-calendar/events?${params.toString()}`
      )
    } catch (err) {
      return {
        error: `Failed to list events: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const getCalendarEvent = tool({
  ...toolSchemas.getCalendarEvent,
  execute: async ({ calendarId = 'primary', eventId }) => {
    try {
      return await googleWorkspaceApiCall(
        `/api/google-calendar/events/${eventId}?calendar_id=${calendarId}`
      )
    } catch (err) {
      return {
        error: `Failed to get event: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const createCalendarEvent = tool({
  ...toolSchemas.createCalendarEvent,
  execute: async ({
    calendarId = 'primary',
    summary,
    start,
    end,
    description,
    location,
    attendees,
  }) => {
    try {
      return await googleWorkspaceApiCall('/api/google-calendar/events', {
        method: 'POST',
        body: {
          calendar_id: calendarId,
          summary,
          start,
          end,
          description: description || undefined,
          location: location || undefined,
          attendees: attendees || undefined,
        },
      })
    } catch (err) {
      return {
        error: `Failed to create event: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const updateCalendarEvent = tool({
  ...toolSchemas.updateCalendarEvent,
  execute: async ({
    calendarId = 'primary',
    eventId,
    summary,
    start,
    end,
    description,
    location,
  }) => {
    try {
      return await googleWorkspaceApiCall(
        `/api/google-calendar/events/${eventId}`,
        {
          method: 'PUT',
          body: {
            calendar_id: calendarId,
            summary: summary || undefined,
            start: start || undefined,
            end: end || undefined,
            description: description || undefined,
            location: location || undefined,
          },
        }
      )
    } catch (err) {
      return {
        error: `Failed to update event: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const deleteCalendarEvent = tool({
  ...toolSchemas.deleteCalendarEvent,
  execute: async ({ calendarId = 'primary', eventId }) => {
    try {
      return await googleWorkspaceApiCall(
        `/api/google-calendar/events/${eventId}?calendar_id=${calendarId}`,
        {
          method: 'DELETE',
        }
      )
    } catch (err) {
      return {
        error: `Failed to delete event: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// =========================================================================
// Google Drive tools
// =========================================================================
export const listDriveFiles = tool({
  ...toolSchemas.listDriveFiles,
  execute: async ({ query, maxResults = 25, mimeType }) => {
    try {
      const params = new URLSearchParams()
      params.append('max_results', String(maxResults))
      if (query) params.append('query', query)
      if (mimeType) params.append('mime_type', mimeType)
      return await googleWorkspaceApiCall(
        `/api/google-drive/files?${params.toString()}`
      )
    } catch (err) {
      return {
        error: `Failed to list files: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const getDriveFile = tool({
  ...toolSchemas.getDriveFile,
  execute: async ({ fileId }) => {
    try {
      return await googleWorkspaceApiCall(
        `/api/google-drive/files/${fileId}/metadata`
      )
    } catch (err) {
      return {
        error: `Failed to get file: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const searchDriveFiles = tool({
  ...toolSchemas.searchDriveFiles,
  execute: async ({ query, maxResults = 25 }) => {
    try {
      const params = new URLSearchParams()
      params.append('query', query)
      params.append('max_results', String(maxResults))
      return await googleWorkspaceApiCall(
        `/api/google-drive/search?${params.toString()}`
      )
    } catch (err) {
      return {
        error: `Failed to search files: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const downloadDriveFileContent = tool({
  ...toolSchemas.downloadDriveFileContent,
  execute: async ({ fileId }) => {
    try {
      return await googleWorkspaceApiCall(
        `/api/google-drive/files/${fileId}/content`
      )
    } catch (err) {
      return {
        error: `Failed to download file: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const uploadDriveFile = tool({
  ...toolSchemas.uploadDriveFile,
  execute: async ({ name, content, mimeType = 'text/plain', parentId }) => {
    try {
      return await googleWorkspaceApiCall('/api/google-drive/files', {
        method: 'POST',
        body: {
          name,
          content,
          mime_type: mimeType,
          parent_id: parentId || undefined,
        },
      })
    } catch (err) {
      return {
        error: `Failed to upload file: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const createDriveFolder = tool({
  ...toolSchemas.createDriveFolder,
  execute: async ({ name, parentId }) => {
    try {
      return await googleWorkspaceApiCall('/api/google-drive/folders', {
        method: 'POST',
        body: {
          name,
          parent_id: parentId || undefined,
        },
      })
    } catch (err) {
      return {
        error: `Failed to create folder: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// =========================================================================
// Google Docs tools
// =========================================================================
export const getDocContent = tool({
  ...toolSchemas.getDocContent,
  execute: async ({ documentId }) => {
    try {
      return await googleWorkspaceApiCall(
        `/api/google-docs/documents/${documentId}`
      )
    } catch (err) {
      return {
        error: `Failed to get document: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const createDoc = tool({
  ...toolSchemas.createDoc,
  execute: async ({ title, bodyText }) => {
    try {
      return await googleWorkspaceApiCall('/api/google-docs/documents', {
        method: 'POST',
        body: {
          title,
          body_text: bodyText || undefined,
        },
      })
    } catch (err) {
      return {
        error: `Failed to create document: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const appendToDoc = tool({
  ...toolSchemas.appendToDoc,
  execute: async ({ documentId, text }) => {
    try {
      return await googleWorkspaceApiCall(
        `/api/google-docs/documents/${documentId}/append`,
        {
          method: 'POST',
          body: { text },
        }
      )
    } catch (err) {
      return {
        error: `Failed to append to document: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

// =========================================================================
// Google Contacts tools
// =========================================================================
export const listContacts = tool({
  ...toolSchemas.listContacts,
  execute: async ({ maxResults = 25 }) => {
    try {
      const params = new URLSearchParams()
      params.append('max_results', String(maxResults))
      return await googleWorkspaceApiCall(
        `/api/google-contacts/contacts?${params.toString()}`
      )
    } catch (err) {
      return {
        error: `Failed to list contacts: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const getContact = tool({
  ...toolSchemas.getContact,
  execute: async ({ resourceName }) => {
    try {
      return await googleWorkspaceApiCall(
        `/api/google-contacts/contacts/${resourceName}`
      )
    } catch (err) {
      return {
        error: `Failed to get contact: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const searchContacts = tool({
  ...toolSchemas.searchContacts,
  execute: async ({ query }) => {
    try {
      const params = new URLSearchParams()
      params.append('query', query)
      return await googleWorkspaceApiCall(
        `/api/google-contacts/search?${params.toString()}`
      )
    } catch (err) {
      return {
        error: `Failed to search contacts: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const createContact = tool({
  ...toolSchemas.createContact,
  execute: async ({ givenName, familyName, email, phone }) => {
    try {
      return await googleWorkspaceApiCall('/api/google-contacts/contacts', {
        method: 'POST',
        body: {
          given_name: givenName,
          family_name: familyName || undefined,
          email: email || undefined,
          phone: phone || undefined,
        },
      })
    } catch (err) {
      return {
        error: `Failed to create contact: ${err instanceof Error ? err.message : String(err)}`,
      }
    }
  },
})

export const commonTools = {
  getRangeSize,
  fetchDataFromUrl,
  listConnectors,
  queryDatabase,
  sendEmail,
  sendSlackMessage,
  queryStripeCustomers,
  queryStripePayments,
  queryStripeInvoices,
  queryStripeSubscriptions,
  getStripeBalance,
  queryStripeProducts,
  queryStripePayouts,
  webSearch,
  listGmailMessages,
  getGmailMessage,
  searchGmailMessages,
  listGmailLabels,
  // Google Calendar (read)
  listCalendars,
  listCalendarEvents,
  getCalendarEvent,
  // Google Drive (read)
  listDriveFiles,
  getDriveFile,
  searchDriveFiles,
  downloadDriveFileContent,
  // Google Docs (read)
  getDocContent,
  // Google Contacts (read)
  listContacts,
  getContact,
  searchContacts,
}
